import React, { useState } from 'react';
import { Building2 } from 'lucide-react';
import { ClientService } from '../services/clientService';
import { Modal, ModalBody, ModalFooter, Input, Dropdown, Button, Checkbox } from './ui';

const NewClientModal = ({ isOpen, onClose, onClientCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    clientStatus: 'Active Client',
    clientOf: '',
    leadSource: '',
    servicesProvided: [],
    address: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const fieldOptions = ClientService.getFieldOptions();

  // Convert field options to dropdown format
  const clientStatusOptions = fieldOptions.clientStatus.map(status => ({
    value: status,
    label: status
  }));

  const leadSourceOptions = [
    { value: '', label: 'None' },
    ...fieldOptions.leadSource.map(source => ({
      value: source,
      label: source
    }))
  ];

  const clientOfOptions = [
    { value: '', label: 'Select...' },
    { value: 'AB Civil', label: 'AB Civil' },
    { value: 'AB Civil NC', label: 'AB Civil NC' }
  ];

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Client name is required';
    }

    if (!formData.clientStatus) {
      newErrors.clientStatus = 'Status is required';
    }

    if (!formData.clientOf) {
      newErrors.clientOf = 'Client Of is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const newClient = await ClientService.createClient({
        name: formData.name,
        clientStatus: formData.clientStatus,
        clientOf: formData.clientOf,
        leadSource: formData.leadSource || null,
        servicesProvided: formData.servicesProvided.length > 0 ? formData.servicesProvided : null,
        address: formData.address || null,
      });

      onClientCreated(newClient);
      handleClose();
    } catch (error) {
      console.error('Error creating client:', error);
      setErrors({ submit: 'Failed to create client. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      clientStatus: 'Active Client',
      clientOf: '',
      leadSource: '',
      servicesProvided: [],
      address: '',
    });
    setErrors({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear related errors
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleServiceToggle = (service) => {
    setFormData((prev) => {
      const servicesProvided = prev.servicesProvided.includes(service)
        ? prev.servicesProvided.filter((s) => s !== service)
        : [...prev.servicesProvided, service];

      return {
        ...prev,
        servicesProvided,
      };
    });
  };

  return (
    <Modal trackAs="NewClientModal" isOpen={isOpen} onClose={handleClose} size="lg">
      {/* Custom Header with Icon and Title */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--cta-orange)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Building2 size={28} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)',
              }}
            >
              New Client
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
              }}
            >
              Add a new client to your database
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Error message */}
          {errors.submit && (
            <div
              style={{
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--danger)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Two column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* Left Column: Basic Information */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Basic Information
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Client Name */}
                <Input
                  label="Client Name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter client name"
                  error={errors.name}
                  required
                  icon={<Building2 size={16} />}
                />

                {/* Client Status */}
                <Dropdown
                  label="Status"
                  options={clientStatusOptions}
                  value={formData.clientStatus}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, clientStatus: value }));
                    if (errors.clientStatus) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.clientStatus;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Select status"
                  error={errors.clientStatus}
                  required
                />

                {/* Client Of */}
                <Dropdown
                  label="Client Of"
                  options={clientOfOptions}
                  value={formData.clientOf}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, clientOf: value }));
                    if (errors.clientOf) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.clientOf;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Select..."
                  error={errors.clientOf}
                  required
                />

                {/* Lead Source */}
                <Dropdown
                  label="Lead Source"
                  options={leadSourceOptions}
                  value={formData.leadSource}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, leadSource: value }));
                  }}
                  placeholder="Select lead source..."
                />
              </div>
            </div>

            {/* Right Column: Details */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Details
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Services Provided */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-300)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    Services Provided
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {fieldOptions.servicesProvided.map((service) => (
                      <Checkbox
                        key={service}
                        label={service}
                        checked={formData.servicesProvided.includes(service)}
                        onChange={() => handleServiceToggle(service)}
                      />
                    ))}
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label
                    htmlFor="address"
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-300)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    Address
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Enter client address..."
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--gray-200)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color var(--transition-fast)',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--cta-blue)';
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--gray-600)';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--space-3)',
              width: '100%',
            }}
          >
            <Button variant="ghost" onClick={handleClose} type="button" disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={loading} disabled={loading}>
              {loading ? 'Creating...' : 'Create Client'}
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default NewClientModal;
